import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';

// Auth components
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import ForgotPassword from './components/ForgotPassword';
import ChangePassword from './components/ChangePassword';
import Onboarding from './components/Onboarding';

// Settings hook
import { useSettings } from './hooks/useSettings';

// Layout components
import Navigation from './components/Navigation';
import Footer from './components/Footer';

// Page components
import MesEleves from './components/MesEleves';
import MesNotes from './components/MesNotes';
import MesGroupes from './components/MesGroupes';
import GroupeDetail from './components/GroupeDetail';
import Reglages from './components/Reglages';

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, mustChangePassword, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Verification de l'authentification...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <AuthenticationFlow />;
  }
  
  // If authenticated but must change password, show change password page
  if (mustChangePassword) {
    return <ChangePassword 
      onPasswordChanged={() => {
        // No need to reload - AuthContext will update user state automatically
      }} 
      isForced={true} 
    />;
  }
  
  return children;
};

// Authentication Flow Component  
const AuthenticationFlow = () => {
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const handleLoginSuccess = () => {
    // Force reload to trigger auth check with new token
    window.location.reload();
  };

  // Listen for custom event from Login component
  useEffect(() => {
    const handleShowForgotPassword = () => setShowForgotPassword(true);
    window.addEventListener('showForgotPassword', handleShowForgotPassword);
    return () => window.removeEventListener('showForgotPassword', handleShowForgotPassword);
  }, []);

  if (showForgotPassword) {
    return (
      <ForgotPassword 
        onBack={() => setShowForgotPassword(false)}
        onSuccess={handleLoginSuccess}
      />
    );
  }

  return <Login onLoginSuccess={handleLoginSuccess} />;
};

// Redirect component to handle slug changes
const PatientRouteHandler = () => {
  // No need for redirect logic here, Navigation component handles it
  return <MesEleves />;
};

// Main App Layout
const AppLayout = () => {
  const { getPatientSlug } = useSettings();
  const { user } = useAuth();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const patientSlug = getPatientSlug();
  
  // Check if onboarding should be shown on mount
  useEffect(() => {
    if (user && user.id) {
      const forceOnboarding = process.env.REACT_APP_FORCE_ONBOARDING === 'true';
      const onboardingCompletedKey = `onboarding_completed_${user.id}`;
      const onboardingCompleted = localStorage.getItem(onboardingCompletedKey);
      
      // Check if user just changed password and needs onboarding
      const showOnboardingKey = `show_onboarding_${user.id}`;
      const shouldShowOnboarding = localStorage.getItem(showOnboardingKey);
      
      if (forceOnboarding || (!onboardingCompleted && shouldShowOnboarding)) {
        setShowOnboarding(true);
        // Clear the trigger flag now that onboarding is showing
        localStorage.removeItem(showOnboardingKey);
      }
    }
  }, [user]);
  
  const handleOnboardingComplete = () => {
    if (user && user.id) {
      localStorage.setItem(`onboarding_completed_${user.id}`, 'true');
    }
    setShowOnboarding(false);
  };
  
  const handleOnboardingSkip = () => {
    if (user && user.id) {
      localStorage.setItem(`onboarding_completed_${user.id}`, 'true');
    }
    setShowOnboarding(false);
  };
  
  const handleShowHelp = () => {
    if (user && user.id) {
      localStorage.removeItem(`onboarding_completed_${user.id}`);
      localStorage.setItem(`show_onboarding_${user.id}`, 'true');
      setShowOnboarding(true);
    }
  };
  
  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      <main className="flex-1 bg-gradient-to-br from-slate-50 to-blue-50">
        <Routes>
          {/* Dynamic patient route */}
          <Route path={`/${patientSlug}`} element={<PatientRouteHandler />} />
          
          {/* Legacy routes - redirect to current slug */}
          <Route path="/eleves" element={<PatientRouteHandler />} />
          <Route path="/clients" element={<PatientRouteHandler />} />
          <Route path="/patients" element={<PatientRouteHandler />} />
          <Route path="/beneficiaires" element={<PatientRouteHandler />} />
          <Route path="/apprenants" element={<PatientRouteHandler />} />
          <Route path="/employes" element={<PatientRouteHandler />} />
          
          {/* Other routes */}
          <Route path="/notes" element={<MesNotes />} />
          <Route path="/groupes" element={<MesGroupes />} />
          <Route path="/groupes/:id" element={<GroupeDetail />} />
          <Route path="/reglages" element={<Reglages />} />
          <Route path="/" element={<Navigate to={`/${patientSlug}`} replace />} />
          <Route path="*" element={<Navigate to={`/${patientSlug}`} replace />} />
        </Routes>
      </main>
      <Footer onShowHelp={handleShowHelp} />
      
      {/* Onboarding overlay */}
      {showOnboarding && (
        <Onboarding 
          onComplete={handleOnboardingComplete}
          onSkip={handleOnboardingSkip}
        />
      )}
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;
